import { useEffect, useMemo, useState } from 'react'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { useMultiSelect } from '../../../../hooks/useMultiSelect'
import { useApprovalSearchParams } from '../../hooks/useApprovalSearchParams'
import { buildApprovalUserOptions } from '../../utils/approvalFilterOptions'
import {
  approveTimesheet,
  listTimesheetApprovalUsers,
  listTimesheets,
  rejectTimesheet,
  bulkApproveTimesheets,
  bulkRejectTimesheets,
} from '../../../project-management/timesheet/api/timesheetApi'

const DEFAULT_PAGE_SIZE = 10

function toIsoDate(value) {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getCurrentMonthRange() {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), 1)
  return {
    from: toIsoDate(start),
    to: toIsoDate(today),
  }
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function buildTimesheetNo(id, entryDate) {
  const year = entryDate ? new Date(entryDate).getFullYear() : new Date().getFullYear()
  const safeYear = Number.isFinite(year) ? year : new Date().getFullYear()
  return `TS-${safeYear}-${String(id).padStart(4, '0')}`
}

export function useTimesheetApprovalScreen() {
  const { searchParams, updateParam, setPageParam } = useApprovalSearchParams()
  const initialRange = useMemo(() => getCurrentMonthRange(), [])

  const page = Number(searchParams.get('page') || 1)
  const searchText = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || 'PENDING'
  const userFilter = searchParams.get('user_id') || 'all'
  const fromDate = searchParams.get('fromDate') ?? initialRange.from
  const toDate = searchParams.get('toDate') ?? initialRange.to

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [userOptions, setUserOptions] = useState([])
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [totals, setTotals] = useState({ pending: 0, approved: 0, rejected: 0, totalHours: 0 })
  const [actionError, setActionError] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [activeActionId, setActiveActionId] = useState(null)
  const [refreshTick, setRefreshTick] = useState(0)
  
  const { isMultiSelectMode, selectedIds, toggleMultiSelectMode, toggleSelection, selectAll, clearSelection } = useMultiSelect()

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      includeTotal: true,
      archive: false,
    }
    if (searchText.trim()) params.search = searchText.trim()
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (statusFilter !== 'all') params.approval_status = statusFilter
    if (userFilter !== 'all') params.user_id = Number(userFilter)
    return params
  }, [page, pageSize, statusFilter, userFilter, fromDate, toDate, searchText])

  useEffect(() => {
    let active = true
    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await listTimesheets(queryParams)
        if (!active) return
        const items = Array.isArray(response?.items) ? response.items : []
        setRows(items)
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
        setTotals(response?.approvalSummary || { pending: 0, approved: 0, rejected: 0, totalHours: 0 })
      } catch (requestError) {
        if (!active) return
        setRows([])
        setError(extractApiErrorMessage(requestError, 'Failed to load approval queue.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadRows()
    return () => {
      active = false
    }
  }, [page, pageSize, queryParams, refreshTick])

  useEffect(() => {
    let active = true

    async function loadUserOptions() {
      try {
        const options = await listTimesheetApprovalUsers({
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          approval_status: statusFilter === 'all' ? undefined : statusFilter,
        })
        if (!active) return
        setUserOptions(Array.isArray(options) ? options : [])
      } catch {
        if (!active) return
        // Retain the previous options so a transient failure cannot erase the
        // visible selection. The URL remains the source of truth.
      }
    }

    loadUserOptions()
    return () => {
      active = false
    }
  }, [fromDate, toDate, statusFilter])

  const mappedRows = useMemo(() => {
    return rows.map((row) => ({
      id: Number(row.id),
      timesheetNo: buildTimesheetNo(Number(row.id), row.entryDate),
      entryDate: formatDate(row.entryDate),
      userName: row.userName || '-',
      projectId: row.projectId ? Number(row.projectId) : null,
      projectName: row.projectName || '-',
      jobId: row.jobId ? Number(row.jobId) : null,
      jobName: row.jobName || '-',
      taskId: row.taskId ? Number(row.taskId) : null,
      taskName: row.taskName || '-',
      activityDescription: row.activityDescription || '',
      hoursLogged: Number(row.hoursLogged || 0),
      approvalStatus: String(row.approvalStatus || '').toUpperCase() || 'PENDING',
      canApprove: Boolean(row.canApprove),
      hasDateChanges: Boolean(row.hasDateChanges || (row.dateChangeCount && Number(row.dateChangeCount) > 0)),
    }))
  }, [rows])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!mappedRows.length) return 0
    return (page - 1) * pageSize + 1
  }, [mappedRows.length, page, pageSize])

  const rangeEnd = useMemo(() => {
    if (!mappedRows.length) return 0
    return rangeStart + mappedRows.length - 1
  }, [mappedRows.length, rangeStart])

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPageParam(nextPage)
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) goToPage(page + 1)
  }

  async function runApprovalAction(timesheetId, actionType) {
    setActionError('')
    setActionMessage('')
    setActiveActionId(timesheetId)
    try {
      if (actionType === 'approve') {
        await approveTimesheet(timesheetId)
        setActionMessage('Timesheet approved successfully.')
      } else {
        await rejectTimesheet(timesheetId)
        setActionMessage('Timesheet rejected successfully.')
      }
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Action failed.'))
    } finally {
      setActiveActionId(null)
    }
  }


  async function runBulkApprovalAction(actionType) {
    if (selectedIds.size === 0) return
    const ids = Array.from(selectedIds)
    setActionError('')
    setActionMessage('')
    try {
      if (actionType === 'approve') {
        await bulkApproveTimesheets({ ids })
        setActionMessage(`Successfully approved ${ids.length} timesheet(s).`)
      } else {
        await bulkRejectTimesheets({ ids })
        setActionMessage(`Successfully rejected ${ids.length} timesheet(s).`)
      }
      clearSelection()
      setRefreshTick((prev) => prev + 1)
    } catch (requestError) {
      setActionError(extractApiErrorMessage(requestError, 'Bulk action failed.'))
    }
  }

  return {
    state: {
      isLoading,
      error,
      actionError,
      actionMessage,
      activeActionId,
      rows: mappedRows,
      page,
      totalPages,
      pageTokens: buildPaginationTokens(page, totalPages),
      totalCount: meta.total,
      hasMore: meta.hasMore,
      rangeStart,
      rangeEnd,
      searchText,
      statusFilter,
      userFilter,
      userOptions: buildApprovalUserOptions(userOptions, userFilter),
      fromDate,
      toDate,
      totals,
      isMultiSelectMode,
      selectedIds,
    },
    actions: {
      setSearchText: (value) => updateParam('search', value),
      setStatusFilter: (value) => updateParam('status', value),
      setUserFilter: (value) => updateParam('user_id', value),
      setFromDate: (value) => updateParam('fromDate', value),
      setToDate: (value) => updateParam('toDate', value),
      goToPreviousPage,
      goToNextPage,
      goToPage,
      approveTimesheet: (timesheetId) => runApprovalAction(timesheetId, 'approve'),
      rejectTimesheet: (timesheetId) => runApprovalAction(timesheetId, 'reject'),
      toggleMultiSelectMode,
      toggleSelection,
      selectAll: () => {
        const selectable = mappedRows.filter(r => r.approvalStatus === 'PENDING' && r.canApprove).map(r => r.id)
        selectAll(selectable)
      },
      bulkApprove: () => runBulkApprovalAction('approve'),
      bulkReject: () => runBulkApprovalAction('reject'),
    },
  }
}
